import { purchaseReportAPI } from './api';
import { toast } from 'react-toastify';

export const purchaseReportActions = {
    fetchReport: async (filters = {}) => {
        try {
            const params = {
                page: filters.page || 1,
                limit: filters.limit || 10,
                start_date: filters.startDate,
                end_date: filters.endDate,
                supplier_id: filters.supplierId,
                search: filters.search,
                sort: filters.sort || 'newest'
            };
            
            // Clean up params - remove null/undefined values
            Object.keys(params).forEach(key => {
                if (params[key] === null || params[key] === undefined || params[key] === '') {
                    delete params[key];
                }
            });
            
            console.log('Fetching report with params:', params);
            
            const response = await purchaseReportAPI.getReport(params);
            
            if (response.status) {
                // If the API returns the data in a different structure, adapt it
                const reportData = response.data || [];
                const reportSummary = response.summary || {};
                
                // Calculate summary from the data if not provided by API
                const calculatedSummary = purchaseReportActions.calculateReportSummary(reportData);
                
                return {
                    success: true,
                    data: reportData,
                    summary: {
                        totalOrders: reportSummary.totalOrders || calculatedSummary.totalOrders,
                        totalAmount: reportSummary.totalAmount || calculatedSummary.totalAmount,
                        totalPaid: reportSummary.totalPaid || calculatedSummary.totalPaid,
                        totalDue: reportSummary.totalDue || calculatedSummary.totalDue,
                        totalTax: reportSummary.totalTax || calculatedSummary.totalTax,
                        averageOrder: reportSummary.averageOrder || calculatedSummary.averageOrder,
                        totalStockQuantity: calculatedSummary.totalStockQuantity
                    },
                    total: response.total || reportData.length || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || 1,
                    filters: response.filters || {}
                };
            } else {
                toast.error(response.message || 'Failed to fetch report');
                return {
                    success: false,
                    data: [],
                    summary: {
                        totalOrders: 0,
                        totalAmount: 0,
                        totalPaid: 0,
                        totalDue: 0,
                        totalTax: 0,
                        averageOrder: 0,
                        totalStockQuantity: 0
                    },
                    total: 0,
                    page: 1,
                    totalPages: 1,
                    error: response.message
                };
            }
        } catch (error) {
            console.error('Error fetching purchase report:', error);
            
            if (error.response) {
                toast.error(`Server error: ${error.response.status} - ${error.response.statusText}`);
            } else if (error.request) {
                toast.error('No response from server. Please check your network connection.');
            } else {
                toast.error('Failed to fetch purchase report: ' + error.message);
            }
            
            return {
                success: false,
                data: [],
                summary: {
                    totalOrders: 0,
                    totalAmount: 0,
                    totalPaid: 0,
                    totalDue: 0,
                    totalTax: 0,
                    averageOrder: 0,
                    totalStockQuantity: 0
                },
                total: 0,
                page: 1,
                totalPages: 1,
                error: error.message
            };
        }
    },
    
    // Calculate summary from report data
    calculateReportSummary: (data) => {
        if (!data || data.length === 0) {
            return {
                totalOrders: 0,
                totalAmount: 0,
                totalPaid: 0,
                totalDue: 0,
                totalTax: 0,
                averageOrder: 0,
                totalStockQuantity: 0
            };
        }
        
        const totalOrders = data.length;
        let totalAmount = 0;
        let totalPaid = 0;
        let totalDue = 0;
        let totalTax = 0;
        let totalStockQuantity = 0;
        
        data.forEach(item => {
            // Sum up all the values from each product
            totalAmount += item.total_purchase_amount || 0;
            totalPaid += item.total_paid_amount || 0;
            totalDue += item.total_due_amount || 0;
            totalTax += item.total_tax || 0;
            totalStockQuantity += item.total_in_stock || 0;
        });
        
        const averageOrder = totalOrders > 0 ? totalAmount / totalOrders : 0;
        
        return {
            totalOrders,
            totalAmount,
            totalPaid,
            totalDue,
            totalTax,
            averageOrder,
            totalStockQuantity
        };
    },
    
    // Fetch suppliers for dropdown
    fetchSuppliers: async () => {
        try {
            const response = await purchaseReportAPI.getSuppliers();
            debugger;
            if (response.status) {
                return {
                    success: true,
                    data: response.data
                };
            } else {
                return {
                    success: false,
                    data: [],
                    message: response.message
                };
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            return {
                success: false,
                data: [],
                message: 'Failed to fetch suppliers'
            };
        }
    },
    
    // Export report
    exportReport: async (exportType, filters = {}) => {
        try {
            const exportData = {
                export_type: exportType,
                start_date: filters.startDate,
                end_date: filters.endDate,
                supplier_id: filters.supplierId,
                search: filters.search
            };
            
            const response = await purchaseReportAPI.exportReport(exportData);
            
            if (response.status) {
                toast.success(`${exportType.toUpperCase()} export generated successfully`);
                
                // Trigger download
                if (response.data && response.data.download_url && response.data.download_url !== '#') {
                    window.open(response.data.download_url, '_blank');
                }
                
                return {
                    success: true,
                    data: response.data
                };
            } else {
                toast.error(response.message || `Failed to export ${exportType}`);
                return {
                    success: false,
                    message: response.message || `Failed to export ${exportType}`
                };
            }
        } catch (error) {
            console.error(`Error exporting ${exportType}:`, error);
            toast.error(`Failed to export ${exportType}`);
            return {
                success: false,
                message: `Export failed: ${error.message}`
            };
        }
    },
    
    // Print report
    printReport: async (filters = {}) => {
        try {
            await purchaseReportAPI.printReport(filters);
            toast.success('Report sent to printer');
            return { success: true };
        } catch (error) {
            console.error('Error printing report:', error);
            toast.error('Failed to print report');
            return { success: false };
        }
    },
    
    // Refresh report
    refreshReport: async (currentFilters) => {
        return await purchaseReportActions.fetchReport(currentFilters);
    }
};